import { useState, useMemo } from 'react';
import { Search, Lightbulb, Palette, BookOpen, Sparkles, Info, X } from 'lucide-react';
import { Card } from '@/components/common';
import { FlagImage } from '@/components/flag';
import { useStatsStore } from '@/store';
import { cn } from '@/lib/utils';
import countries from '@/data/countries.json';
import flagInfoData from '@/data/flag-info.json';
import type { Country, Region, FlagInfo, MasteryLevel } from '@/types';

const flagInfo = flagInfoData as Record<string, FlagInfo>;
const allCountries = countries as Country[];
const regions: Region[] = ['Africa', 'Americas', 'Asia', 'Europe', 'Oceania'];

const masteryLevels: { level: MasteryLevel; label: string; color: string; description: string }[] = [
  { level: 'new', label: 'New', color: 'bg-stone-400', description: 'Not seen yet' },
  { level: 'learning', label: 'Learning', color: 'bg-red-500', description: "You've seen it but haven't got it down yet" },
  { level: 'familiar', label: 'Familiar', color: 'bg-amber-500', description: "You've got it right a few times" },
  { level: 'mastered', label: 'Mastered', color: 'bg-emerald-500', description: 'You know it!' },
];

export function LearnPage() {
  const [search, setSearch] = useState('');
  const [selectedRegion, setSelectedRegion] = useState<Region | null>(null);
  const [selectedMastery, setSelectedMastery] = useState<MasteryLevel | null>(null);
  const [selectedCountry, setSelectedCountry] = useState<Country | null>(null);
  const [showLevelInfo, setShowLevelInfo] = useState(false);

  const { getMasteryLevel, getFlagStats } = useStatsStore();

  const filteredCountries = useMemo(() => {
    return allCountries.filter((c) => {
      const matchesSearch =
        !search ||
        c.name.toLowerCase().includes(search.toLowerCase()) ||
        c.officialName.toLowerCase().includes(search.toLowerCase());
      const matchesRegion = !selectedRegion || c.region === selectedRegion;
      const matchesMastery = !selectedMastery || getMasteryLevel(c.code) === selectedMastery;
      return matchesSearch && matchesRegion && matchesMastery;
    });
  }, [search, selectedRegion, selectedMastery, getMasteryLevel]);

  const getMasteryColor = (level: MasteryLevel) => {
    return masteryLevels.find((m) => m.level === level)?.color ?? 'bg-stone-400';
  };

  const info = selectedCountry ? flagInfo[selectedCountry.code] : null;

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold">Learn Flags</h1>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
        <input
          type="text"
          placeholder="Search countries..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-10 pr-4 py-3 rounded-xl border border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-900 text-stone-900 dark:text-stone-100 focus:outline-none focus:border-primary-500"
        />
      </div>

      {/* Region filter */}
      <div className="flex gap-2 overflow-x-auto pb-2 -mx-4 px-4">
        <button
          onClick={() => setSelectedRegion(null)}
          className={cn(
            'px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors',
            !selectedRegion
              ? 'bg-primary-500 text-white'
              : 'bg-stone-100 dark:bg-stone-800 text-stone-700 dark:text-stone-300'
          )}
        >
          All
        </button>
        {regions.map((region) => (
          <button
            key={region}
            onClick={() => setSelectedRegion(region)}
            className={cn(
              'px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors',
              selectedRegion === region
                ? 'bg-primary-500 text-white'
                : 'bg-stone-100 dark:bg-stone-800 text-stone-700 dark:text-stone-300'
            )}
          >
            {region}
          </button>
        ))}
      </div>

      {/* Mastery filter */}
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-stone-700 dark:text-stone-300">Familiarity</span>
          <button
            onClick={() => setShowLevelInfo(true)}
            className="p-1 text-stone-400 hover:text-stone-600 dark:hover:text-stone-300 transition-colors"
            aria-label="What do these levels mean?"
          >
            <Info className="w-4 h-4" />
          </button>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setSelectedMastery(null)}
            className={cn(
              'px-3 py-1.5 rounded-full text-sm font-medium transition-colors',
              !selectedMastery
                ? 'bg-primary-500 text-white'
                : 'bg-stone-100 dark:bg-stone-800 text-stone-600 dark:text-stone-400'
            )}
          >
            All
          </button>
          {masteryLevels.map(({ level, label, color }) => (
            <button
              key={level}
              onClick={() => setSelectedMastery(selectedMastery === level ? null : level)}
              className={cn(
                'flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-colors',
                selectedMastery === level
                  ? 'bg-primary-500 text-white'
                  : 'bg-stone-100 dark:bg-stone-800 text-stone-600 dark:text-stone-400'
              )}
            >
              <div className={cn('w-2.5 h-2.5 rounded-full', color)} />
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Level info modal */}
      {showLevelInfo && (
        <Card className="p-4 bg-primary-50 dark:bg-primary-900/20 border-primary-200 dark:border-primary-800">
          <div className="flex items-start justify-between gap-2 mb-3">
            <h3 className="font-medium text-primary-900 dark:text-primary-100">Familiarity Levels</h3>
            <button
              onClick={() => setShowLevelInfo(false)}
              className="text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-200"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="space-y-2">
            {masteryLevels.map(({ level, label, color, description }) => (
              <div key={level} className="flex items-center gap-2">
                <div className={cn('w-3 h-3 rounded-full shrink-0', color)} />
                <div className="text-sm">
                  <span className="font-medium text-primary-900 dark:text-primary-100">{label}</span>
                  <span className="text-primary-700 dark:text-primary-300"> — {description}</span>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Country count */}
      <p className="text-sm text-stone-500">
        {filteredCountries.length} countries
      </p>

      {/* Countries grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
        {filteredCountries.map((country) => {
          const mastery = getMasteryLevel(country.code);
          const stats = getFlagStats(country.code);
          const accuracy = stats
            ? Math.round((stats.timesCorrect / stats.timesShown) * 100)
            : null;

          return (
            <Card
              key={country.code}
              variant="interactive"
              onClick={() => setSelectedCountry(country)}
              className="p-4"
            >
              <div className="flex flex-col items-center gap-3">
                <div className="relative">
                  <FlagImage
                    countryCode={country.code}
                    countryName={country.name}
                    size="lg"
                  />
                  <div
                    className={cn(
                      'absolute top-0 right-0 w-3.5 h-3.5 rounded-full ring-2 ring-white dark:ring-stone-900',
                      getMasteryColor(mastery)
                    )}
                  />
                </div>
                <div className="text-center">
                  <div className="font-medium text-sm text-stone-900 dark:text-stone-100">{country.name}</div>
                  {accuracy !== null && (
                    <div className="text-xs text-stone-500">{accuracy}% accuracy</div>
                  )}
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      {/* Country detail modal */}
      {selectedCountry && (
        <div
          className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-4"
          onClick={() => setSelectedCountry(null)}
        >
          <Card
            className="w-full max-w-md max-h-[80vh] overflow-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="space-y-4">
              <div className="flex justify-center">
                <FlagImage
                  countryCode={selectedCountry.code}
                  countryName={selectedCountry.name}
                  size="xl"
                />
              </div>

              <div className="text-center">
                <h2 className="text-2xl font-bold">{selectedCountry.name}</h2>
                <p className="text-stone-500">{selectedCountry.officialName}</p>
              </div>

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <div className="text-stone-500">Capital</div>
                  <div className="font-medium">
                    {selectedCountry.capital || 'N/A'}
                  </div>
                </div>
                <div>
                  <div className="text-stone-500">Region</div>
                  <div className="font-medium">{selectedCountry.region}</div>
                </div>
                <div className="col-span-2">
                  <div className="text-stone-500">Subregion</div>
                  <div className="font-medium">{selectedCountry.subregion}</div>
                </div>
              </div>

              {/* Flag info */}
              {info && (
                <div className="space-y-4 pt-4 border-t border-stone-200 dark:border-stone-700">
                  {/* Mnemonic */}
                  <div className="bg-primary-50 dark:bg-primary-900/20 rounded-lg p-3">
                    <div className="flex items-center gap-2 text-primary-600 dark:text-primary-400 text-sm font-medium mb-1">
                      <Lightbulb className="w-4 h-4" />
                      Memory Tip
                    </div>
                    <p className="text-sm">{info.mnemonic}</p>
                  </div>

                  {/* Colors */}
                  <div>
                    <div className="flex items-center gap-2 text-stone-500 text-sm mb-2">
                      <Palette className="w-4 h-4" />
                      Colors
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {info.colors.map((color) => (
                        <span
                          key={color}
                          className="px-2 py-1 bg-stone-100 dark:bg-stone-800 rounded text-sm capitalize"
                        >
                          {color}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Symbolism */}
                  <div>
                    <div className="flex items-center gap-2 text-stone-500 text-sm mb-1">
                      <BookOpen className="w-4 h-4" />
                      Symbolism
                    </div>
                    <p className="text-sm">{info.symbolism}</p>
                  </div>

                  {/* Fun fact */}
                  <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-3">
                    <div className="flex items-center gap-2 text-purple-600 dark:text-purple-400 text-sm font-medium mb-1">
                      <Sparkles className="w-4 h-4" />
                      Fun Fact
                    </div>
                    <p className="text-sm">{info.funFact}</p>
                  </div>

                  {/* Similar flags */}
                  {info.similarFlags.length > 0 && (
                    <div>
                      <div className="text-stone-500 text-sm mb-2">Similar Flags</div>
                      <div className="flex gap-2 flex-wrap">
                        {info.similarFlags.map((code) => {
                          const country = allCountries.find((c) => c.code === code);
                          if (!country) return null;
                          return (
                            <button
                              key={code}
                              onClick={() => setSelectedCountry(country)}
                              className="flex items-center gap-2 px-2 py-1 bg-stone-100 dark:bg-stone-800 rounded hover:bg-stone-200 dark:hover:bg-stone-700 transition-colors"
                            >
                              <FlagImage
                                countryCode={code}
                                countryName={country.name}
                                size="xs"
                              />
                              <span className="text-sm">{country.name}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Stats if available */}
              {getFlagStats(selectedCountry.code) && (
                <div className="pt-4 border-t border-stone-200 dark:border-stone-700">
                  <div className="text-sm text-stone-500 mb-2">Your progress</div>
                  <div className="flex justify-between">
                    <span>
                      Seen {getFlagStats(selectedCountry.code)?.timesShown} times
                    </span>
                    <span className="font-medium">
                      {getFlagStats(selectedCountry.code)?.timesShown &&
                        Math.round(
                          ((getFlagStats(selectedCountry.code)?.timesCorrect ?? 0) /
                            (getFlagStats(selectedCountry.code)?.timesShown ?? 1)) *
                            100
                        )}
                      % accuracy
                    </span>
                  </div>
                </div>
              )}

              <button
                onClick={() => setSelectedCountry(null)}
                className="w-full py-3 text-center text-primary-600 dark:text-primary-400 font-medium"
              >
                Close
              </button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
